export const DEFAULT_MAX_DISTANCE = 1;
export const MIN_MAX_DISTANCE = 0;
export const MAX_MAX_DISTANCE = 5;

/**
 * @param {unknown} value
 * @returns {number}
 */
export function clampMaxDistance(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return DEFAULT_MAX_DISTANCE;
    return Math.min(MAX_MAX_DISTANCE, Math.max(MIN_MAX_DISTANCE, Math.round(num)));
}

/**
 * @param {Array<{ from: string, to: string }>|null|undefined} connections
 * @returns {Map<string, Set<string>>}
 */
export function buildEntityAdjacency(connections) {
    const adjacency = new Map();

    const addEdge = (left, right) => {
        if (!left || !right || left === right) return;
        if (!adjacency.has(left)) adjacency.set(left, new Set());
        if (!adjacency.has(right)) adjacency.set(right, new Set());
        adjacency.get(left).add(right);
        adjacency.get(right).add(left);
    };

    for (const edge of connections || []) {
        addEdge(edge?.from, edge?.to);
    }

    return adjacency;
}

/**
 * BFS expansion from seed entity ids along chart arrow connections.
 *
 * @param {Iterable<string>|Set<string>} seedIds
 * @param {Array<{ from: string, to: string }>|null|undefined} connections
 * @param {number} maxDistance hops beyond seeds (0 = seeds only)
 * @returns {Set<string>}
 */
export function expandEntityIdsByChartDistance(seedIds, connections, maxDistance) {
    const seeds = seedIds instanceof Set ? seedIds : new Set(seedIds);
    const hops = clampMaxDistance(maxDistance);

    if (hops === 0) return new Set(seeds);

    const adjacency = buildEntityAdjacency(connections);
    const visible = new Set(seeds);
    let frontier = new Set(seeds);

    for (let hop = 0; hop < hops; hop += 1) {
        const nextFrontier = new Set();
        for (const entityId of frontier) {
            const neighbors = adjacency.get(entityId);
            if (!neighbors) continue;
            for (const neighbor of neighbors) {
                if (visible.has(neighbor)) continue;
                visible.add(neighbor);
                nextFrontier.add(neighbor);
            }
        }
        frontier = nextFrontier;
        if (frontier.size === 0) break;
    }

    return visible;
}
